// Импорт вставленного вручную PGN со стартового экрана в синтетическую CoachGame.
// Пользователь может вставить как полный PGN с тегами ([White]/[Black]/…), так и
// голую последовательность ходов («1. e4 e5 2. Nf3») — chess.js в loose-режиме
// (strict:false внутри loadGameFromPgn) понимает оба. Битый/пустой ввод →
// PgnImportError, который экран показывает человеческим i18n-сообщением.
import { Chess } from 'chess.js';
import { EvalStore } from '@ext/core/evals';
import { loadGameFromPgn } from '@ext/core/game-load';
import { normalizeGame, type CoachGame } from '@ext/core/game-sources';

export class PgnImportError extends Error {}

/**
 * Детерминированный 32-bit hash (FNV-1a) → стабильный id одинакового PGN.
 * Один и тот же текст всегда даёт один id (дедуп/консистентность ссылок разбора).
 */
export function hashPgn(pgn: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < pgn.length; i++) {
    h ^= pgn.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/**
 * Разобрать вставленный PGN в синтетическую CoachGame (site 'pgn'). Валидация —
 * через штатный loadGameFromPgn (тот же путь, что и у настоящих партий): пустой или
 * нелегальный ввод бросает PgnImportError. id = pgn_<hash> стабилен. Игроки/дата/
 * результат/дебют подтягиваются из тегов PGN, если они есть; без тегов остаются
 * пустыми (разбор покажет «—» и обе стороны, т.к. сторона юзера неизвестна).
 */
/**
 * Санитайзер под «умную» вставку iOS/мессенджеров: длинные тире в результатах
 * (1–0), неразрывные/тонкие пробелы, фигурные кавычки в тегах, невидимые
 * символы — всё приводим к тому, что понимает PGN-парсер.
 */
export function sanitizePgn(raw: string): string {
  return String(raw || '')
    .replace(/[\u2010-\u2015\u2212]/g, '-') // все виды тире → дефис (1–0 → 1-0)
    .replace(/[\u00a0\u2007\u202f]/g, ' ') // неразрывные/тонкие пробелы
    .replace(/[\u200b\u200c\u200d\ufeff]/g, '') // невидимые
    .replace(/[\u201c\u201d\u00ab\u00bb]/g, '"') // фигурные кавычки тегов
    .replace(/\u2026/g, '...') // многоточие-символ
    .replace(/\u00bd/g, '1/2') // ½–½
    .replace(/\r\n?/g, '\n')
    .trim();
}

/**
 * Диагностика битой записи: реплей ходов по одному. Возвращает первый нечитаемый
 * токен и номер хода + валидный префикс партии (для «разобрать до этого хода»).
 */
export interface PgnDiagnosis {
  badSan: string;
  moveNo: number;
  white: boolean;
  prefixPgn: string | null; // валидная часть до ошибки (null если ходов не набралось)
}

export function diagnosePgn(raw: string): PgnDiagnosis | null {
  const text = sanitizePgn(raw).replace(/\[[^\]]*\]/g, ' ');
  const tokens = text
    .replace(/\{[^}]*\}/g, ' ')
    .split(/\s+/)
    .filter((t) => t && !/^\d+\.+$/.test(t) && !/^(1-0|0-1|1\/2-1\/2|\*)$/.test(t))
    .map((t) => t.replace(/^\d+\.+/, ''))
    .filter(Boolean);
  const chess = new Chess();
  for (let i = 0; i < tokens.length; i++) {
    try {
      chess.move(tokens[i]!, { strict: false });
    } catch {
      const good = chess.history();
      return {
        badSan: tokens[i]!,
        moveNo: Math.floor(i / 2) + 1,
        white: i % 2 === 0,
        prefixPgn: good.length >= 4 ? good.map((m, j) => (j % 2 === 0 ? `${j / 2 + 1}. ${m}` : m)).join(' ') : null,
      };
    }
  }
  return null;
}

export function coachGameFromPgn(raw: string): CoachGame {
  const pgn = sanitizePgn(raw);
  if (!pgn) throw new PgnImportError('empty');
  let text = pgn;
  try {
    loadGameFromPgn(text, null, new EvalStore());
  } catch (e) {
    // многострочный список ходов без тегов: пробуем одной строкой
    const collapsed = text.includes('[') ? null : text.replace(/\s+/g, ' ').trim();
    if (!collapsed) throw new PgnImportError(e instanceof Error ? e.message : String(e));
    try {
      loadGameFromPgn(collapsed, null, new EvalStore());
      text = collapsed;
    } catch (e2) {
      throw new PgnImportError(e2 instanceof Error ? e2.message : String(e2));
    }
  }
  return normalizeGame({ site: 'pgn', id: `pgn_${hashPgn(text)}`, pgn: text });
}
